import { Button, Modal } from '@rtkwlf/ui';
import * as React from 'react';
import { noOp } from '../../constants';

type Props = {
  visible: boolean;
  title: string;
  onCancel: () => void;
  showCancel?: boolean;
  onOk: () => void;
  okTitle?: string;
  okDisabled?: boolean;
  loading?: boolean;
  closable?: boolean;
  id?: string;
  showConfirm?: boolean;
  confirmOnRefresh?: boolean;
  isFormDirty?: boolean;
};

/**
 * A wrapper of the Modal component based on existing usage. Tweaks include:
 *  - footer is always customized OnboardingModalFooter
 *  - submit callback is handled by OnboardingModalFooter
 *  - onOk is noOp (this basically disables submission on 'enter' keydown)
 *  - default to be closable from top right
 */
export const OnboardingModal: React.FunctionComponent<Props> = ({
  visible,
  title,
  onOk,
  onCancel,
  showCancel = true,
  okTitle = 'Save',
  okDisabled,
  loading,
  closable = true,
  children,
  id,
  showConfirm = false,
  isFormDirty = false,
}) => {
  const closeModal = React.useCallback(() => {
    // Don't show confirm if the parent didn't specify it or if the form isn't dirty
    if (!showConfirm || !isFormDirty) {
      onCancel();
    } else {
      if (
        window.confirm(
          'All newly entered data will be lost. Are you sure you want to close?',
        )
      ) {
        onCancel();
      }
    }
  }, [showConfirm, isFormDirty, onCancel]);

  React.useEffect(() => {
    const confirmReload = (e: any) => {
      // Cancel the event
      // If you prevent default behavior in Mozilla Firefox prompt will always be shown
      e.preventDefault();
      // Chrome requires returnValue to be set
      e.returnValue = '';
    };
    if (visible && isFormDirty && showConfirm) {
      window.addEventListener('beforeunload', confirmReload);
      return () => {
        window.removeEventListener('beforeunload', confirmReload);
      };
    }
  }, [showConfirm, isFormDirty, visible]);

  return (
    <Modal
      visible={visible}
      title={title}
      onCancel={closeModal}
      closable={closable}
      onOk={noOp}
      footer={
        <>
          {showCancel && (
            <Button
              type="primary"
              ghost
              onClick={closeModal}
              disabled={loading}
            >
              Cancel
            </Button>
          )}
          <Button
            type="primary"
            data-cy="onboardingModalSave"
            onClick={onOk}
            disabled={okDisabled}
            data-id={id} //support multiple modals in cypress
          >
            {okTitle}
          </Button>
        </>
      }
    >
      {children}
    </Modal>
  );
};
